import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PageChangeEvent, SortSettings } from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { IGridResult, IGridState, PageSize } from 'src/app/core/models/base';
import { IWeekReportingObject, IWeekReportingObjectSearchRequest } from 'src/app/time-tracking/models/week-reporting-object';
import { TimeEntryService } from 'src/app/time-tracking/services/time-entry.service';
import { TimeObject, TimeSpan } from '../helpers/time-span';
import { Store } from '@ngrx/store';
import { AppStateInterface } from 'src/app/stores/global/appState.interface';
import { currentEmployeeSelector } from 'src/app/stores/global/current-employee/current-employee.store';
import { PermissionStorageService } from 'src/app/authentication/storages/permission-storage.service';
import { TimeEntryAccessService } from 'src/app/time-tracking/services/time-entry-access.service';

@UntilDestroy()
@Component({
    selector: 'app-week-reporting-grid',
    templateUrl: './week-reporting-grid.component.html',
    styleUrls: ['./week-reporting-grid.component.css'],
    standalone: false
})
export class WeekReportingGridComponent implements OnInit {

  constructor(
    private timeEntryService: TimeEntryService,
    private store: Store<AppStateInterface>,
    private permissionStorage: PermissionStorageService,
    private accessService: TimeEntryAccessService
  ) {
    this.store.select(currentEmployeeSelector)
      .pipe(untilDestroyed(this))
      .subscribe(employee => this.currentEmployeeId = employee?.id ?? null);
    this.accessService.manageableEmployeeIds$
      .pipe(untilDestroyed(this))
      .subscribe(ids => this.manageableIds = ids);
  }

  private currentEmployeeId: string | null = null;
  private manageableIds = new Set<string>();

  // Mirrors the server rule: one's own week takes SelfSignEntry, somebody else's SignEntry plus
  // the reach of the plain rights (hierarchy, or everyone with SearchAll).
  canSign(weekReportingObject: IWeekReportingObject): boolean {
    const permissions = this.permissionStorage.getPermissions() ?? [];
    return weekReportingObject.employeeId === this.currentEmployeeId
      ? permissions.includes('Permissions.WeekReportingObject.SelfSignEntry')
      : permissions.includes('Permissions.WeekReportingObject.SignEntry') && this.manageableIds.has(weekReportingObject.employeeId);
  }

  @Input() weekReportingObjects :IGridResult<IWeekReportingObject[]>;
  @Input() public showFlexPreWorkChanges: boolean = true;
  // Controls the day-level flex-/pre-work-time columns in the nested day report grid.
  @Input() public showDayFlexPreWorkChanges: boolean = false;
  @Input() public showtimeSupplementTimeChanges: boolean = false;
  // Overtime column, driven by the tenant setting hasOvertime.
  @Input() public showOvertime: boolean = false;
  @Input() public searchRequest: IWeekReportingObjectSearchRequest;  
  @Input() public isAdminView: boolean = false;
  // Enables clicking the date column header to toggle the (server-side) date sort direction.
  @Input() public sortable: boolean = false;
  @Input() public set totalWorkTime(totalWorkTime: string) {
    if(totalWorkTime){
      this.totalWorkTimeObj = TimeSpan.parse(totalWorkTime);
    }
  }
  @Input() public set totalFlexTime(totalFlexTime: string) {
    if(totalFlexTime){
      this.totalFlexTimeObj = TimeSpan.parse(totalFlexTime);
    }
  }
  @Input() public set totalPreWorkTime(totalPreWorkTime: string) {
    if(totalPreWorkTime){
      this.totalPreWorkTimeObj = TimeSpan.parse(totalPreWorkTime);
    }
  }
  // Grand totals over the whole selection (all pages), shown as a second footer line.
  @Input() public set grandWorkTime(grandWorkTime: string) {
    if(grandWorkTime){
      this.grandWorkTimeObj = TimeSpan.parse(grandWorkTime);
    }
  }
  @Input() public set grandFlexTime(grandFlexTime: string) {
    if(grandFlexTime){
      this.grandFlexTimeObj = TimeSpan.parse(grandFlexTime);
    }
  }
  @Input() public set grandPreWorkTime(grandPreWorkTime: string) {
    if(grandPreWorkTime){
      this.grandPreWorkTimeObj = TimeSpan.parse(grandPreWorkTime);
    }
  }
  @Output() onUpdateGrid: EventEmitter<undefined> = new EventEmitter();

  totalWorkTimeObj: TimeSpan;
  totalFlexTimeObj: TimeSpan;
  totalPreWorkTimeObj: TimeSpan;
  grandWorkTimeObj: TimeSpan;
  grandFlexTimeObj: TimeSpan;
  grandPreWorkTimeObj: TimeSpan;

  get hasGrandTotals(): boolean {
    return !!(this.grandWorkTimeObj || this.grandFlexTimeObj || this.grandPreWorkTimeObj);
  }

  public sortSettings: SortSettings = { allowUnsort: false, mode: 'single' };
  public sort: SortDescriptor[] = [];

  ngOnInit(): void {
  }

  onSortChange(sort: SortDescriptor[]) {
    this.sort = sort;
    const dir = sort[0]?.dir ?? 'desc';
    this.searchRequest.orderBy = [`StartDate ${dir}`];
    this.searchRequest.pageNumber = 1;
    this.gridState.skip = 0;
    this.onUpdateGrid.emit();
  }

  mask = "00:00";
  editMask = "AB:CD";
  maskRules: { [key: string]: RegExp } = {
    A: /[0-2]+/,
    B: /[0-9]+/,
    C: /[0-5]+/,
    D: /[0-9]+/,
  };

  gridState: IGridState = {
    pageSize: 25,
    skip: 0,
  };

  get pageSizes() {
    return PageSize;
  }

  onPageSizeChange(pageSize: number) {
    this.onPageChange({ skip: this.gridState.skip, take: pageSize });
  }

  onGridReload() {
    this.onUpdateGrid.emit();
  }

  onPageChange({ skip, take }: PageChangeEvent) {
    const page = Math.round((skip + take) / take);

    this.gridState.skip = skip;
    this.gridState.pageSize = take;

    this.searchRequest.pageNumber = page;
    this.searchRequest.pageSize = take;

    this.onUpdateGrid.emit();
  }

  onSignWeek(id: string){
    this.timeEntryService.signWeek({weekReportingObjectId: id})
    .pipe(untilDestroyed(this))
    .subscribe(response => {
      this.onUpdateGrid.emit();
    })
  }

  canSignWeek (endDate: Date): boolean{
    var result = new Date(endDate) < new Date();
    return result;
  }

  onConfirmWeek(id: string){
    this.timeEntryService.confirmWeek({weekReportingObjectId: id})
    .pipe(untilDestroyed(this))
    .subscribe(response => {
      this.onUpdateGrid.emit();
    })
  }

  canConfirmWeek(endDate: Date): boolean{
    return new Date(endDate) < new Date();
  }

  allDaysConfirmed(week: IWeekReportingObject): boolean{
    return week.dayReportingObjects?.every(day => day.isConfirmed || day.isSigned) ?? false;
  }

  getTimeObjClass(timeObject: TimeObject){
      if(timeObject?.factor < 0){
        return "error"
      }
    }

} 
